import { useState, useEffect, useCallback } from 'react'
import { ICategory } from '@/app/[locale]/_types'

interface IOffsetTops {
  id: string
  offsetTop: number
  offsetHeight: number
  parentId?: string
}

// Local debounce utility function with cancel support
const debounce = <T extends (...args: any[]) => any>(
  func: T,
  delay: number
): T & { cancel: () => void } => {
  let timeout: ReturnType<typeof setTimeout>
  const debounced = ((...args: Parameters<T>) => {
    clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), delay)
  }) as T & { cancel: () => void }

  debounced.cancel = () => {
    clearTimeout(timeout)
  }

  return debounced
}

const SCROLL_CONFIG = {
  OFFSET_ADJUSTMENT: 150,
  TIMEOUTS: {
    CALCULATE_OFFSETS: 500,
  },
  DEBOUNCE: {
    RESIZE: 200,
  },
} as const

export const useCategoryOffsets = (data: ICategory[]) => {
  const [categoriesOffsetTops, setOffsetTops] = useState<IOffsetTops[]>([])

  const calculateOffsets = useCallback(() => {
    const offsets: IOffsetTops[] = []
    const offsetAdjustment = SCROLL_CONFIG.OFFSET_ADJUSTMENT

    data?.forEach((category) => {
      const categoryElement = document.getElementById(category.slug)

      if (categoryElement) {
        offsets.push({
          id: category.slug,
          offsetTop: categoryElement.offsetTop - offsetAdjustment,
          offsetHeight: categoryElement.offsetHeight,
        })
      }

      category.child_categories?.forEach((child) => {
        const childElement = document.getElementById(child.slug)

        if (childElement) {
          offsets.push({
            id: child.slug,
            offsetTop: childElement.offsetTop - offsetAdjustment,
            offsetHeight: childElement.offsetHeight,
            parentId: category.slug,
          })
        }
      })
    })

    setOffsetTops(offsets)
  }, [data])

  useEffect(() => {
    // Run initially after a short delay to allow elements to render
    const initialOffsetTimeoutId = setTimeout(
      () => calculateOffsets(),
      SCROLL_CONFIG.TIMEOUTS.CALCULATE_OFFSETS
    )

    const handleResize = debounce(
      () => calculateOffsets(),
      SCROLL_CONFIG.DEBOUNCE.RESIZE
    )
    window.addEventListener('resize', handleResize as EventListener)

    return () => {
      clearTimeout(initialOffsetTimeoutId)
      window.removeEventListener('resize', handleResize as EventListener)
      handleResize.cancel()
    }
  }, [calculateOffsets])

  return categoriesOffsetTops
}